import { useState, useEffect, useMemo } from "react";
import MediaFrame, { resolveMediaUrl } from "./MediaFrame";
import { getProjectCoverCandidates } from "@/domain/projectCover";
import { useStudio, useLookups } from "@/store/StudioContext";

/**
 * Vista pura de la portada del proyecto con fallback interactivo y no cíclico.
 * 
 * - Si un candidato falla al cargar, avanza secuencialmente al siguiente candidato.
 * - Solo muestra "Sin imagen asignada" cuando se han agotado todos los candidatos válidos.
 * - No realiza bucles ni reintentos infinitos.
 */
export const ProjectCoverView = ({
  project,
  state,
  assetById,
  className = "",
  testId,
  fit = "contain",
  showBadge = true,
  emptyMessage,
}) => {
  const candidates = useMemo(
    () => getProjectCoverCandidates(project, state, assetById),
    [project, state, assetById]
  );

  const [attemptIndex, setAttemptIndex] = useState(0);

  // Reiniciar el índice de intento al cambiar de proyecto
  useEffect(() => {
    setAttemptIndex(0);
  }, [project?.id]);

  // Si el candidato actual tiene una URL vacía o que no se puede resolver, avanzar
  useEffect(() => {
    if (attemptIndex < candidates.length) {
      const cand = candidates[attemptIndex];
      if (!cand || !cand.url || !resolveMediaUrl(cand.url)) {
        setAttemptIndex((prev) => (prev < candidates.length ? prev + 1 : prev));
      }
    }
  }, [attemptIndex, candidates]);

  const currentCandidate =
    attemptIndex < candidates.length ? candidates[attemptIndex] : null;

  const handleCandidateError = () => {
    // Avanzar de forma estrictamente monótona para garantizar ausencia de bucles
    setAttemptIndex((prev) => (prev < candidates.length ? prev + 1 : prev));
  };

  return (
    <MediaFrame
      asset={currentCandidate}
      className={className}
      fit={fit}
      showBadge={showBadge}
      testId={testId || (project ? `project-cover-${project.id}` : undefined)}
      emptyMessage={emptyMessage}
      onError={handleCandidateError}
    />
  );
};

/**
 * Componente conectado al contexto global de Studio.
 */
const ProjectCover = ({ state: propState, assetById: propAssetById, ...props }) => {
  const studio = useStudio();
  const lookups = useLookups();

  const effectiveState = propState || studio?.state;
  const effectiveAssetById = propAssetById || lookups?.assetById;

  return (
    <ProjectCoverView
      state={effectiveState}
      assetById={effectiveAssetById}
      {...props}
    />
  );
};

export { ProjectCover };
export default ProjectCover;
